import apiService, { ServiceType } from './api';
import type { ImportStarted } from './importJobApi';

/**
 * Claiming shares back out of the basic book and into a grid.
 *
 * Two calls because it is two screens: `preview` prices the ladder and returns everything
 * wrong with it, `apply` writes it. `apply` recomputes the plan from the same inputs rather
 * than trusting the preview — so sending a doctored preview back changes nothing, and so a
 * stale preview can never be what lands.
 *
 * UNITS: every price is RAW VND. The engine's thousand-VND form is written on the server.
 */

export interface ImportLevel {
    /** Level price, raw VND. */
    price: number;
    /** Shares parked on this level. Always a whole number of lots. */
    quantity: number;
    /** Where the grid would sell this level: `price + takeProfit`, raw VND. */
    takeProfitPrice: number;
    /** Profit per share against the declared cost. Negative: this level sells at a loss. */
    pnlPerShare: number;
}

/**
 * A priced warning. Both kinds are opposite mistakes and neither is refused — a user cutting
 * a loss on purpose, or gathering a position in one go, is allowed to say so.
 */
export interface ImportWarning {
    kind: 'SELLS_AT_A_LOSS' | 'BUYS_IMMEDIATELY';
    /** Money, raw VND: a number to weigh, not an adjective. */
    amount: number;
    detail: string;
}

/** Something that must be fixed before `apply` will write anything. */
export interface ImportProblem {
    field: string;
    message: string;
}

export interface ImportPreview {
    symbol: string;
    levels: ImportLevel[];
    totalQuantity: number;
    warnings: ImportWarning[];
    problems: ImportProblem[];
    /** Shares the basic book actually has. The ladder cannot claim more. */
    available: number;
    /** The broker's cost of the unclaimed shares, raw VND per share. Null: cannot be imported. */
    averagePrice: number | null;
}

export interface GridImportRequest {
    symbol: string;
    exchange?: string;
    quantity: number;
    /** Top of the ladder, raw VND — their choice, not derived. */
    origin: number;
    /** Levels they asked for. */
    levelCount: number;
    intervalTicks: number;
    /** Derived by the server: take-profit IS the gap. Leave it out. */
    takeProfitTicks?: number;
    /** Latest market price, raw VND. Omit and the "buys immediately" warning is not raised. */
    marketPrice?: number;
    /** Shares per level, highest first. Omit for an even spread; the total must equal qty. */
    quantities?: number[];
    /** Cash to put in beside the shares, raw VND: what the empty levels buy with. */
    cash?: number;
}

const core = () => apiService.getService(ServiceType.CORE);

export async function previewGridImport(
    dataConfigId: string,
    body: GridImportRequest,
): Promise<ImportPreview> {
    const { data } = await core().post<ImportPreview>(`/grid/${dataConfigId}/import/preview`, body);
    return data;
}

/**
 * Starts the import job (202): the wallet and the ladder are written in the background, the
 * database and the bot together. 409 IMPORT_IN_PROGRESS while another one is open.
 */
export async function applyGridImport(
    dataConfigId: string,
    body: GridImportRequest,
): Promise<ImportStarted> {
    const { data } = await core().post<ImportStarted>(`/grid/${dataConfigId}/import`, body);
    return data;
}

/**
 * Takes one symbol out of the grid without selling (202): its shares go back to the basic book
 * and its allocation is released. 409 while the bot runs or another import is open.
 */
export async function endGridSymbolFromBroker(
    dataConfigId: string,
    symbol: string,
    confirm: string,
): Promise<ImportStarted> {
    const { data } = await core().post<ImportStarted>(
        `/grid/${dataConfigId}/symbol/${encodeURIComponent(symbol)}/end-from-broker`,
        { confirm },
    );
    return data;
}
